import { UUID } from "@bitory-gmbh/nidavellir";
import { backendApi } from "@/services/backend/api";
import {
  Extra,
  IncidentalCost,
  Pricing,
} from "@/services/backend/vbs/pricing/types/pricing";

const pricingService = backendApi
  .enhanceEndpoints({
    addTagTypes: ["VbsPricing"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      vbsPricingShow: build.query<Pricing, ShowRequest>({
        query: (request) => ({
          url: "/vbs/pricing",
          method: "GET",
          params: request,
        }),
        providesTags: (result) =>
          result ? [{ type: "VbsPricing", id: result.id }] : [],
      }),
      vbsPricingUpdateTreatmentPriceUseCalculation: build.mutation<
        Pricing,
        UpdateTreatmentPriceUseCalculationRequest
      >({
        query: (request) => ({
          url: "/vbs/pricing/treatment-price-use-calculation",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "VbsPricing", id: request.id },
          "VbsOffer",
          "VbsOrder",
          "VbsConfirmation",
          "VbsWorkOrder",
          "VbsPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      vbsPricingUpdateFromCalculation: build.mutation<
        Pricing,
        UpdateFromCalculationRequest
      >({
        query: (request) => ({
          url: "/vbs/pricing/update-from-calculation",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "VbsPricing", id: request.id },
          "VbsOffer",
          "VbsOrder",
          "VbsConfirmation",
          "VbsWorkOrder",
          "VbsPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      vbsPricingUpdateTreatmentPriceBasePrice: build.mutation<
        Pricing,
        UpdateTreatmentPriceBasePriceRequest
      >({
        query: (request) => ({
          url: "/vbs/pricing/treatment-price/base-price",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "VbsPricing", id: request.id },
          "VbsOffer",
          "VbsOrder",
          "VbsConfirmation",
          "VbsWorkOrder",
          "VbsPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      vbsPricingUpdateTreatmentPriceRelativeDiscount: build.mutation<
        Pricing,
        UpdateTreatmentPriceRelativeDiscountRequest
      >({
        query: (request) => ({
          url: "/vbs/pricing/treatment-price/relative-discount",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "VbsPricing", id: request.id },
          "VbsOffer",
          "VbsOrder",
          "VbsConfirmation",
          "VbsWorkOrder",
          "VbsPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      vbsPricingUpdateTreatmentPriceAbsoluteDiscount: build.mutation<
        Pricing,
        UpdateTreatmentPriceAbsoluteDiscountRequest
      >({
        query: (request) => ({
          url: "/vbs/pricing/treatment-price/absolute-discount",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "VbsPricing", id: request.id },
          "VbsOffer",
          "VbsOrder",
          "VbsConfirmation",
          "VbsWorkOrder",
          "VbsPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      vbsPricingUpdateTreatmentPricePostExecutionReduction: build.mutation<
        Pricing,
        UpdateTreatmentPricePostExecutionReductionRequest
      >({
        query: (request) => ({
          url: "/vbs/pricing/treatment-price/post-execution-reduction",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "VbsPricing", id: request.id },
          "VbsOffer",
          "VbsOrder",
          "VbsConfirmation",
          "VbsWorkOrder",
          "VbsPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      vbsPricingUpdateIncidentalCostUseCalculation: build.mutation<
        Pricing,
        UpdateIncidentalCostUseCalculationRequest
      >({
        query: (request) => ({
          url: "/vbs/pricing/incidental-cost-use-calculation",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "VbsPricing", id: request.id },
          "VbsOffer",
          "VbsOrder",
          "VbsConfirmation",
          "VbsWorkOrder",
          "VbsPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      vbsPricingUpdateIncidentalCost: build.mutation<
        Pricing,
        UpdateIncidentalCostRequest
      >({
        query: (request) => ({
          url: "/vbs/pricing/incidental-cost",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "VbsPricing", id: request.id },
          "VbsOffer",
          "VbsOrder",
          "VbsConfirmation",
          "VbsWorkOrder",
          "VbsPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      vbsPricingUpdateExtras: build.mutation<Pricing, UpdateExtrasRequest>({
        query: (request) => ({
          url: "/vbs/pricing/extras",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "VbsPricing", id: request.id },
          "VbsOffer",
          "VbsOrder",
          "VbsConfirmation",
          "VbsWorkOrder",
          "VbsPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
    }),
  });

export const {
  useVbsPricingShowQuery,
  useVbsPricingUpdateTreatmentPriceUseCalculationMutation,
  useVbsPricingUpdateFromCalculationMutation,
  useVbsPricingUpdateTreatmentPriceBasePriceMutation,
  useVbsPricingUpdateTreatmentPriceRelativeDiscountMutation,
  useVbsPricingUpdateTreatmentPriceAbsoluteDiscountMutation,
  useVbsPricingUpdateTreatmentPricePostExecutionReductionMutation,
  useVbsPricingUpdateIncidentalCostUseCalculationMutation,
  useVbsPricingUpdateIncidentalCostMutation,
  useVbsPricingUpdateExtrasMutation,
} = pricingService;

export type ShowRequest = {
  id: UUID;
};

export type UpdateTreatmentPriceUseCalculationRequest = {
  id: UUID;
  value: boolean;
};

export type UpdateFromCalculationRequest = {
  id: UUID;
  calculationId: UUID;
};

export type UpdateTreatmentPriceBasePriceRequest = {
  id: UUID;
  value: number;
};

export type UpdateTreatmentPriceRelativeDiscountRequest = {
  id: UUID;
  active: boolean;
  percentage: number;
};

export type UpdateTreatmentPriceAbsoluteDiscountRequest = {
  id: UUID;
  active: boolean;
  amount: number;
};

export type UpdateTreatmentPricePostExecutionReductionRequest = {
  id: UUID;
  label: string;
  amount: number;
};

export type UpdateIncidentalCostUseCalculationRequest = {
  id: UUID;
  value: boolean;
};

export type UpdateIncidentalCostRequest = {
  id: UUID;
  incidentalCost: IncidentalCost;
};

export type UpdateExtrasRequest = {
  id: UUID;
  extras: Extra[];
};
